import { initializeApp, getApps, getApp } from "firebase/app";
import { getAuth } from "firebase/auth";
import {
  initializeFirestore,
  getFirestore,
  type Firestore,
} from "firebase/firestore";
import { getStorage, ref, uploadBytes, getDownloadURL, deleteObject } from "firebase/storage";
import { getFunctions } from "firebase/functions";
import { getAnalytics, isSupported as isAnalyticsSupported } from "firebase/analytics";
import { logger } from "../services/logger";

// Type definition for Firebase compat API on window object
interface FirebaseCompat {
  initializeApp: (config: unknown) => unknown;
  auth: () => {
    currentUser: unknown;
    onAuthStateChanged: (callback: (user: unknown) => void, errorCallback?: (error: Error) => void) => () => void;
    signInAnonymously: () => Promise<unknown>;
  };
  apps: unknown[];
}

// Extend Window interface to include Firebase compat API
declare global {
  interface Window {
    firebase?: FirebaseCompat;
    __FIREBASE_CONFIG__?: {
      apiKey: string;
      authDomain: string;
      projectId: string;
      storageBucket: string;
      messagingSenderId: string;
      appId: string;
    };
  }
}

// Firebase configuration from environment variables
// These should be set in .env file (see .env.example for template)
export const firebaseConfig = {
  apiKey: process.env.REACT_APP_FIREBASE_API_KEY || "",
  authDomain: process.env.REACT_APP_FIREBASE_AUTH_DOMAIN || "",
  projectId: process.env.REACT_APP_FIREBASE_PROJECT_ID || "",
  storageBucket: process.env.REACT_APP_FIREBASE_STORAGE_BUCKET || "",
  messagingSenderId: process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID || "",
  appId: process.env.REACT_APP_FIREBASE_APP_ID || "",
  measurementId: process.env.REACT_APP_FIREBASE_MEASUREMENT_ID || ""
};

// Validate that required Firebase config values are present
const validateFirebaseConfig = () => {
  const requiredFields = [
    'apiKey',
    'authDomain',
    'projectId',
    'storageBucket',
    'messagingSenderId',
    'appId'
  ] as const;

  const missingFields: string[] = [];

  requiredFields.forEach(field => {
    if (!firebaseConfig[field]) {
      missingFields.push(`REACT_APP_FIREBASE_${field.toUpperCase().replace(/([A-Z])/g, '_$1').slice(1)}`);
    }
  });

  if (missingFields.length > 0) {
    const errorMessage = `
🚨 Firebase Configuration Error

Missing required environment variables:
${missingFields.map(field => `  - ${field}`).join('\n')}

Please create a .env file in the root directory with these variables.
See .env.example for a template.

Example:
REACT_APP_FIREBASE_API_KEY=your_api_key_here
REACT_APP_FIREBASE_AUTH_DOMAIN=your_auth_domain_here
...

For React apps, environment variables must start with REACT_APP_
    `.trim();

    if (process.env.NODE_ENV === 'development') {
      // In development, log error but don't throw - allow app to start
      // This prevents the app from being stuck in loading state
      logger.error('Firebase configuration is incomplete', new Error(errorMessage), {
        missingFields,
        scope: 'firebase-config'
      });
      logger.warn('Firebase config missing - app will start but authentication will not work', {
        scope: 'firebase-config',
        action: 'Please create a .env file with Firebase configuration'
      });
      // Don't throw in development - let the app start and show the error
      return;
    } else {
      // In production, throw error to prevent app from running with invalid config
      logger.error('Firebase configuration is incomplete', new Error(errorMessage), {
        missingFields,
        scope: 'firebase-config'
      });
      throw new Error('Firebase configuration is incomplete. Please check environment variables.');
    }
  }
};

// Validate configuration before initializing
validateFirebaseConfig();

const isTestEnvironment = typeof process !== 'undefined' && process.env.NODE_ENV === 'test';

// Determine if we're in a development environment
export const isLocalhost = typeof window !== 'undefined' && 
  (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1');

// Only initialize Firebase if it hasn't been initialized yet
// Wrap in try-catch to prevent app crash if config is invalid
let app: ReturnType<typeof initializeApp>;
try {
  app = getApps().length === 0 ? initializeApp(firebaseConfig) : getApp();
  
  // Log Firebase initialization status
  logger.info("Firebase initialized", {
    projectId: firebaseConfig.projectId,
    storageBucket: firebaseConfig.storageBucket,
    authDomain: firebaseConfig.authDomain,
    scope: 'firebase-init'
  });
} catch (error) {
  logger.error('Firebase initialization failed', error as Error, {
    scope: 'firebase-init'
  });
  // In development, create a dummy app to prevent crashes
  if (process.env.NODE_ENV === 'development') {
    logger.warn('Firebase failed to initialize - using fallback. Please check your .env file.', {
      scope: 'firebase-init',
      error: error instanceof Error ? error.message : String(error)
    });
    // Try to get existing app or create minimal config
    try {
      app = getApp();
    } catch {
      // If that fails, create with minimal config
      app = initializeApp({
        apiKey: 'dev-placeholder',
        authDomain: 'dev-placeholder',
        projectId: 'dev-placeholder',
        storageBucket: 'dev-placeholder',
        messagingSenderId: 'dev-placeholder',
        appId: 'dev-placeholder'
      });
    }
  } else {
    throw error; // In production, throw to prevent running with invalid config
  }
}

export const auth = getAuth(app);

// Firestore — force long polling for the listen stream. This sidesteps
// the firebase-js-sdk 11.x "Unexpected state (ID: ca9 / b815)" assertion
// failures, which originate in the WebChannel-based listen stream when
// listeners are set up + torn down rapidly (React 18 StrictMode double-
// mount in dev, HMR, fast tab navigation, weak networks behind proxies).
//
// We tried `experimentalAutoDetectLongPolling: true` first — but the
// detection only kicks in AFTER WebChannel has been tried and failed,
// by which point the bad listener is already armed. `forceLongPolling`
// uses long polling from the first request and skips WebChannel entirely.
//
// Trade-off: long polling is slightly higher latency than WebChannel for
// real-time updates (~500ms vs ~50ms), but the difference is invisible
// in this app's UI patterns (lists, dashboards) and the fix is decisive.
// If we ever need the WebChannel speed, the right move is to upgrade
// firebase to a patched 11.x release rather than re-enabling it here.
//
// Refs: github.com/firebase/firebase-js-sdk/issues/8250, /9056, /8866
let dbInstance: Firestore;
try {
  dbInstance = initializeFirestore(app, {
    experimentalForceLongPolling: true,
    // Disable Fetch streams (they can also trigger the assertion in
    // some Chromium builds — long polling is the safe path).
    useFetchStreams: false,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  } as any);
} catch (err) {
  // Already-initialized — pick up the existing instance.
  logger.warn('initializeFirestore threw; falling back to getFirestore', {
    scope: 'firebase-init',
    error: err instanceof Error ? err.message : String(err),
  });
  dbInstance = getFirestore(app);
}
export const db = dbInstance;

export const storage = getStorage(app);
export const functions = getFunctions(app, 'us-central1');

// ─── Dev-only DevTools console helpers ───────────────────────────────
// Exposes admin-style callables on `window.__suprie` so they can be
// invoked from the browser console during development. Only attached
// in dev/local builds — production never sees this.
//
// Usage in Chrome DevTools console:
//
//   await window.__suprie.runAudit('petId-here')
//   // → returns + logs the audit JSON
//
//   await window.__suprie.callFn('regenerateHealthStories', { petId: 'petId-here' })
//   // → escape hatch for any other callable
//
// We attach via dynamic import of httpsCallable to avoid loading the
// function-call code in production at all.
if (
  typeof window !== 'undefined' &&
  (process.env.NODE_ENV === 'development' || process.env.NODE_ENV !== 'production')
) {
  void import('firebase/functions').then(({ httpsCallable }) => {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (window as any).__suprie = {
      /**
       * Invoke any callable by name. Returns the data payload (already
       * unwrapped from { data }), so you can `copy(JSON.stringify(...))`
       * the result directly.
       */
      callFn: async (name: string, payload: unknown = {}) => {
        const fn = httpsCallable(functions, name);
        const r = await fn(payload as Record<string, unknown>);
        // Auto-copy stringified result for easy paste-back to chat.
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        try { (window as any).copy?.(JSON.stringify(r.data, null, 2)); } catch { /* clipboard blocked */ }
        // eslint-disable-next-line no-console
        console.log(`✅ ${name} returned (also copied to clipboard):`, r.data);
        return r.data;
      },
      /** Shortcut: body systems data coverage audit. */
      runAudit: async (petId: string) => {
        const fn = httpsCallable(functions, 'auditBodySystemsCoverage');
        const r = await fn({ petId });
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        try { (window as any).copy?.(JSON.stringify(r.data, null, 2)); } catch { /* clipboard blocked */ }
        // eslint-disable-next-line no-console
        console.log('✅ Audit complete (JSON copied to clipboard):', r.data);
        return r.data;
      },
      /**
       * Shortcut: backfill physicalExam onto pre-UDP-6.1.6 vet visits.
       * Pass an optional visitDocId to enrich just one visit (force re-
       * extract — overrides the idempotency check). Without it, processes
       * every vet visit doc that doesn't yet have physicalExam.
       */
      enrichExam: async (petId: string, visitDocId?: string) => {
        const fn = httpsCallable(functions, 'enrichVetVisitWithPhysicalExam');
        const r = await fn(visitDocId ? { petId, visitDocId } : { petId });
        // eslint-disable-next-line no-console
        console.log('✅ Enrichment complete:', r.data);
        return r.data;
      },
    };
    // eslint-disable-next-line no-console
    console.log('%c[suprie-dev] window.__suprie ready — try: window.__suprie.runAudit("petId")', 'color:#5856d6;font-weight:600');
  });
}

// Initialize Firebase Analytics (GA4) in production only
// Uses isSupported() to avoid errors in SSR, tests, or browsers that block analytics
if (typeof window !== 'undefined' && process.env.NODE_ENV === 'production') {
  isAnalyticsSupported().then((supported) => {
    if (supported && firebaseConfig.measurementId) {
      const analytics = getAnalytics(app);
      logger.info('Firebase Analytics initialized', { scope: 'firebase-analytics' });
      // Make available for navigation analytics and other services
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      (window as any).__firebaseAnalytics = analytics;
    }
  }).catch(() => {
    // Analytics blocked (e.g. ad blocker) — silently continue
  });
}

// Log Storage initialization status
try {
  logger.info("Firebase Storage initialized successfully", {
    appName: storage.app.name,
    maxOperationRetryTime: storage.maxOperationRetryTime,
    maxUploadRetryTime: storage.maxUploadRetryTime,
    scope: 'firebase-storage'
  });
} catch (error) {
  logger.error("Error logging Firebase Storage status", error as Error, {
    scope: 'firebase-storage'
  });
}

// Enable more detailed error messages and increase timeouts
storage.maxOperationRetryTime = 120000; // 120 seconds (increased from 60)
storage.maxUploadRetryTime = 300000;   // 300 seconds (5 minutes) for large files

// Use local storage for uploads in development by default to avoid CORS issues
export const useLocalStorageForUploads = false; // Always use Firebase Storage regardless of environment

// Add error handling for debugging
const handleFirebaseErrors = () => {
  try {
    // Log any Firebase errors using logger
    const unsubscribe = auth.onAuthStateChanged((user) => {
      logger.debug('Firebase auth state changed', {
        authenticated: !!user,
        userId: user?.uid,
        userEmail: user?.email,
        scope: 'firebase-auth'
      });
    }, (error) => {
      logger.error('Firebase auth error', error as Error, {
        scope: 'firebase-auth'
      });
    });
    
    // Note: Firebase compat API warnings are expected when using modular API
    // The modular API (v9+) doesn't require compat scripts, so these warnings can be ignored
    // Only check compat in development for debugging purposes
    if (process.env.NODE_ENV === 'development' && typeof window !== 'undefined' && !isTestEnvironment) {
      if (!window.firebase) {
        // This is expected with modular API - suppress warning in production
        logger.debug('Firebase compat not found (expected with modular API)', {
          scope: 'firebase-compat'
        });
      } else {
        logger.debug('Firebase compat detected in window', {
          compatAvailable: !!window.firebase,
          scope: 'firebase-compat'
        });
      }
    }
    
    return unsubscribe;
  } catch (error) {
    logger.error('Error setting up Firebase error handler', error as Error, {
      scope: 'firebase-error-handler'
    });
    return () => {};
  }
};

// Start the error handler
const _unsubscribeFromErrorHandler = handleFirebaseErrors();

// Helper function to check if we're in development mode
export const isDevelopmentMode = () => {
  return process.env.NODE_ENV === 'development' || isLocalhost;
};

// Helper function to ensure Firebase compat API is ready
export const ensureCompatApiReady = (): Promise<boolean> => {
  return new Promise((resolve) => {
    if (isTestEnvironment) {
      resolve(true);
      return;
    }

    if (typeof window !== 'undefined' && window.firebase) {
      resolve(true);
      return;
    }
    
    // Wait up to 5 seconds for compat API to load
    let attempts = 0;
    const maxAttempts = 50; // 5 seconds at 100ms intervals
    
    const checkForCompat = () => {
      attempts++;
      if (typeof window !== 'undefined' && window.firebase) {
        logger.debug('Firebase compat API is ready', { scope: 'firebase-compat' });
        resolve(true);
      } else if (attempts >= maxAttempts) {
        logger.warn('Firebase compat API not available after 5 seconds', {
          scope: 'firebase-compat',
          attempts
        });
        resolve(false);
      } else {
        setTimeout(checkForCompat, 100);
      }
    };
    
    checkForCompat();
  });
};

// Export testing function to verify Firebase Storage connectivity
export const testStorageConnection = async () => {
  const log = logger.createScope('firebase-storage-test');
  
  try {
    log.info('Starting Firebase Storage connectivity test');
    
    // Check if user is authenticated
    if (!auth.currentUser) {
      return { 
        success: false, 
        message: 'User not authenticated', 
        error: 'Please sign in to test storage connectivity' 
      };
    }
    
    log.info('User authenticated', { userId: auth.currentUser.uid });
    
    // Create a small test file
    const testData = new Blob(['connectivity-test'], { type: 'text/plain' });
    const testFile = new File([testData], 'connectivity_test.txt', { type: 'text/plain' });
    const timestamp = Date.now();
    const testPath = `_test/connectivity_${timestamp}.txt`;
    
    log.debug('Test file created', { testPath, fileSize: testFile.size });
    
    // Use modular SDK for testing (same as upload logic)
    const storageRef = ref(storage, testPath);
    
    try {
      log.debug('Starting test upload');
      const _uploadResult = await uploadBytes(storageRef, testFile);
      log.info('Test upload successful');
      
      log.debug('Getting download URL');
      const downloadURL = await getDownloadURL(storageRef);
      log.debug('Download URL obtained', { urlPrefix: downloadURL.substring(0, 50) });
      
      log.debug('Testing download');
      const response = await fetch(downloadURL);
      if (response.ok) {
        const text = await response.text();
        log.info('Download test successful', { content: text });
      } else {
        log.warn('Download test failed', { status: response.status });
      }
      
      log.debug('Cleaning up test file');
      await deleteObject(storageRef);
      log.info('Test file deleted');
      
      return { 
        success: true, 
        message: 'Storage connectivity test passed successfully' 
      };
      
    } catch (uploadError) {
      log.error('Storage test failed', uploadError as Error);
      
      // Provide specific error information
      const errorMessage = (uploadError as Error).message;
      let detailedMessage = 'Storage test failed';
      
      if (errorMessage.includes('storage/unauthorized')) {
        detailedMessage = 'Storage access denied - check Firebase rules';
      } else if (errorMessage.includes('storage/network-error')) {
        detailedMessage = 'Network error - check internet connection';
      } else if (errorMessage.includes('CORS')) {
        detailedMessage = 'CORS error - check Firebase configuration';
      } else if (errorMessage.includes('auth')) {
        detailedMessage = 'Authentication error - please sign in again';
      }
      
      return { 
        success: false, 
        message: detailedMessage,
        error: errorMessage,
        details: {
          errorName: (uploadError as Error).name,
          userUid: auth.currentUser?.uid,
          testPath: testPath,
          fileSize: testFile.size
        }
      };
    }
  } catch (error) {
    log.error('Storage test setup failed', error as Error);
    return { 
      success: false, 
      message: 'Storage test setup failed', 
      error: (error as Error).message 
    };
  }
};

export default app;